import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteChannelMapping,
  getJobsState,
  getSyncState,
  listChannelMappings,
  listEntraUsers,
  listUsers,
  triggerSync,
  triggerTicketmasterEventImport,
  upsertChannelMapping,
} from '../api/client';
import type { UpsertChannelMappingBody } from '../api/client';

export function useChannelMappings() {
  return useQuery({
    queryKey: ['admin', 'channel-mappings'],
    queryFn: listChannelMappings,
    staleTime: 60_000,
  });
}

export function useUpsertChannelMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, body }: { locationId: string; body: UpsertChannelMappingBody }) =>
      upsertChannelMapping(locationId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'channel-mappings'] });
    },
  });
}

export function useDeleteChannelMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: string) => deleteChannelMapping(locationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'channel-mappings'] });
    },
  });
}

export function useSyncState() {
  return useQuery({
    queryKey: ['admin', 'sync-state'],
    queryFn: getSyncState,
    staleTime: 30_000,
  });
}

export function useTriggerSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (forceFull: boolean) => triggerSync(forceFull),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sync-state'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs-state'] });
    },
  });
}

export function useJobsState() {
  return useQuery({
    queryKey: ['admin', 'jobs-state'],
    queryFn: getJobsState,
    staleTime: 30_000,
  });
}

export function useTriggerSportsEventImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => import('../api/client').then((m) => m.triggerSportsEventImport()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs-state'] });
    },
  });
}

export function useTriggerTicketmasterEventImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => triggerTicketmasterEventImport(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'jobs-state'] });
    },
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => listUsers({ limit: 200 }),
    staleTime: 60_000,
  });
}

export function useAdminEntraUsers() {
  return useQuery({
    queryKey: ['admin', 'entra-users', 'all'],
    queryFn: () => listEntraUsers(),
    staleTime: 60_000,
  });
}
